/**
 * English Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const en: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Dashboard',
  'nav.funds': 'Funds',
  'nav.investments': 'Investments',
  'nav.investees': 'Investees',
  'nav.esg': 'ESG Review',
  'nav.compliance': 'Compliance',
  'nav.reports': 'Reports',
  'nav.settings': 'Settings',
  'nav.users': 'Users',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Indicators',
  'nav.taxonomy': 'EU Taxonomy',
  'nav.filings': 'Filings',
  'nav.calendar': 'Calendar',
  'nav.submit': 'Submit Data',
  'nav.submissions': 'My Submissions',
  'nav.benchmarks': 'Benchmarks',
  'nav.messages': 'Messages',
  'nav.portfolio': 'Portfolio',
  'nav.performance': 'Fund Performance',
  'nav.esgSummary': 'ESG Summary',
  'nav.pipeline': 'Pipeline',
  'nav.dueDiligence': 'Due Diligence',
  'nav.impact': 'Impact Metrics',
  'nav.sdg': 'SDG Mapping',
  'nav.audit': 'Audit Trail',
  'nav.snapshots': 'Data Snapshots',
  'nav.knowledgeGraph': 'Knowledge Graph',
  'nav.geographic': 'Geographic',
  'nav.bi': 'BI Studio',
  'nav.ai': 'AI Assistant',
  'nav.tasks': 'Tasks',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Regulatory Monitor',

  // Common Actions
  'action.create': 'Create',
  'action.edit': 'Edit',
  'action.delete': 'Delete',
  'action.save': 'Save',
  'action.cancel': 'Cancel',
  'action.submit': 'Submit',
  'action.approve': 'Approve',
  'action.reject': 'Reject',
  'action.download': 'Download',
  'action.export': 'Export',
  'action.import': 'Import',
  'action.filter': 'Filter',
  'action.search': 'Search',
  'action.refresh': 'Refresh',
  'action.close': 'Close',
  'action.back': 'Back',
  'action.next': 'Next',
  'action.previous': 'Previous',
  'action.viewAll': 'View All',
  'action.viewDetails': 'View Details',
  'action.generate': 'Generate',
  'action.signOut': 'Sign Out',
  'action.addNew': 'Add New',
  'action.invite': 'Invite',

  // Statuses
  'status.draft': 'Draft',
  'status.submitted': 'Submitted',
  'status.validated': 'Validated',
  'status.approved': 'Approved',
  'status.rejected': 'Rejected',
  'status.pending': 'Pending',
  'status.inProgress': 'In Progress',
  'status.overdue': 'Overdue',
  'status.compliant': 'Compliant',
  'status.filed': 'Filed',
  'status.active': 'Active',
  'status.inactive': 'Inactive',
  'status.closed': 'Closed',
  'status.fundraising': 'Fundraising',
  'status.investing': 'Investing',
  'status.harvesting': 'Harvesting',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Portfolio Overview',
  'dashboard.complianceStatus': 'Compliance Status',
  'dashboard.collectionProgress': 'Collection Progress',
  'dashboard.activityFeed': 'Activity',
  'dashboard.quickActions': 'Quick Actions',
  'dashboard.investmentPipeline': 'Investment Pipeline',
  'dashboard.totalFunds': 'Total Funds',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Active Investments',
  'dashboard.totalInvestees': 'Investees',
  'dashboard.recentActivity': 'Recent Activity',
  'dashboard.viewBoard': 'View Board',
  'dashboard.viewAll': 'View All',
  'dashboard.noPipelineData': 'No pipeline data available',
  'dashboard.noCollectionData': 'No collection data',
  'dashboard.noComplianceData': 'No compliance data available',
  'dashboard.noRecentActivity': 'No recent activity',

  // Quick Action Labels
  'action.createFund': 'Create Fund',
  'action.addInvestment': 'Add Investment',
  'action.inviteInvestee': 'Invite Investee',
  'action.generateReport': 'Generate Report',
  'action.sfdrFiling': 'SFDR Filing',
  'action.sendDataRequest': 'Send Data Request',

  // Page Titles
  'page.investments': 'Investments',
  'page.funds': 'Funds',
  'page.esg': 'ESG Data Management',
  'page.compliance': 'Compliance',
  'page.reports': 'Reports',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Total Committed',
  'lp.capitalCalled': 'Capital Called',
  'lp.distributed': 'Distributed',
  'lp.netPosition': 'Net Position',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Fund Performance',
  'lp.esgSummary': 'ESG Summary',
  'lp.reportLibrary': 'Report Library',
  'lp.taxonomyAligned': 'Taxonomy Aligned',
  'lp.paiCoverage': 'PAI Coverage',
  'lp.portfolioPerformance': 'LP Portfolio Performance',

  // Fund Labels
  'fund.name': 'Fund Name',
  'fund.code': 'Fund Code',
  'fund.vintage': 'Vintage Year',
  'fund.size': 'Fund Size',
  'fund.targetSize': 'Target Size',
  'fund.currency': 'Currency',
  'fund.strategy': 'Strategy',
  'fund.status': 'Status',
  'fund.sfdrCategory': 'SFDR Category',
  'fund.geographyFocus': 'Geography Focus',
  'fund.sectorFocus': 'Sector Focus',

  // Form Labels
  'form.name': 'Name',
  'form.email': 'Email',
  'form.country': 'Country',
  'form.amount': 'Amount',
  'form.currency': 'Currency',
  'form.date': 'Date',
  'form.description': 'Description',
  'form.notes': 'Notes',
  'form.type': 'Type',
  'form.period': 'Period',
  'form.year': 'Year',
  'form.required': 'Required',
  'form.optional': 'Optional',

  // Table Headers
  'table.fund': 'Fund',
  'table.commitment': 'Commitment',
  'table.called': 'Called',
  'table.distributed': 'Distributed',
  'table.tvpi': 'TVPI',
  'table.status': 'Status',
  'table.regulation': 'Regulation',
  'table.filingType': 'Filing Type',
  'table.reportingPeriod': 'Period',
  'table.createdAt': 'Created',
  'table.actions': 'Actions',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'EU Taxonomy',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Data Completeness',
  'compliance.nextDeadline': 'Next Deadline',

  // Reports
  'report.quarterly': 'Quarterly Report',
  'report.annual': 'Annual Report',
  'report.paiStatement': 'PAI Statement',
  'report.taxonomyReport': 'Taxonomy Report',
  'report.generatedAt': 'Generated',
  'report.version': 'Version',

  // General
  'general.loading': 'Loading...',
  'general.noData': 'No data available',
  'general.error': 'An error occurred',
  'general.retry': 'Retry',
  'general.total': 'Total',
  'general.page': 'Page',
  'general.of': 'of',
  'general.showing': 'Showing',
  'general.results': 'results',
  'general.language': 'Language',
  'general.english': 'English',
  'general.french': 'French',

  // Tour
  'tour.takeTour': 'Take a Tour',
  'tour.skip': 'Skip',
  'tour.next': 'Next',
  'tour.prev': 'Previous',
  'tour.finish': 'Finish',
};
